import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuthStore } from '../stores/authStore';
import { API_URL } from '../config';

const ERROR_MESSAGES: Record<string, string> = {
  invalid_magic_link: 'That sign-in link has expired or already been used. Request a new one below.',
  missing_token: 'The sign-in link was incomplete. Request a new one below.',
};

type MagicLinkState = 'idle' | 'sending' | 'sent';

export const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [magicEmail, setMagicEmail] = useState('');
  const [magicState, setMagicState] = useState<MagicLinkState>('idle');
  const [sentToEmail, setSentToEmail] = useState('');

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuthStore();

  const urlError = searchParams.get('error');
  const urlErrorMessage = urlError ? ERROR_MESSAGES[urlError] ?? null : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await login(email, password);
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGitHubLogin = () => {
    window.location.href = `${API_URL}/api/v1/auth/github`;
  };

  const handleMagicLinkRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setMagicState('sending');

    try {
      await fetch(`${API_URL}/api/v1/auth/magic-link/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: magicEmail }),
      });
    } catch (err) {
      console.error('Magic link request error:', err);
    }

    // Always show the same success UI — never reveal whether the email exists
    setSentToEmail(magicEmail);
    setMagicState('sent');
    toast.success('Check your inbox for a sign-in link.');
  };

  const handleSendAnother = () => {
    setMagicState('idle');
    setMagicEmail('');
    setSentToEmail('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to RiskLens
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Security scanning platform for open-source repositories
          </p>
        </div>

        {urlErrorMessage && (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded text-sm">
            {urlErrorMessage}
          </div>
        )}

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <input
                id="email"
                name="email"
                type="email"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <input
                id="password"
                name="password"
                type="password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div className="w-full border-t border-gray-300"></div>
            <span className="px-3 text-gray-500 text-sm">or</span>
            <div className="w-full border-t border-gray-300"></div>
          </div>

          <div>
            <button
              type="button"
              onClick={handleGitHubLogin}
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z" clipRule="evenodd" />
              </svg>
              Continue with GitHub
            </button>
          </div>
        </form>

        {/* Magic link section */}
        <div className="space-y-4">
          <div className="flex items-center">
            <div className="w-full border-t border-gray-300"></div>
            <span className="px-3 text-gray-500 text-sm whitespace-nowrap">
              Sign in with Email (no password)
            </span>
            <div className="w-full border-t border-gray-300"></div>
          </div>

          {magicState === 'sent' ? (
            <div className="bg-indigo-50 border border-indigo-200 rounded-md px-4 py-5 text-center space-y-3">
              <div className="text-indigo-700 font-medium text-sm">Check your inbox</div>
              <p className="text-gray-600 text-sm">
                We sent a sign-in link to{' '}
                <span className="font-medium text-gray-900">{sentToEmail}</span>.
                The link expires in 15 minutes.
              </p>
              <p className="text-gray-500 text-xs">
                Don't see it? Check your spam folder.
              </p>
              <button
                type="button"
                onClick={handleSendAnother}
                className="text-indigo-600 hover:text-indigo-500 text-sm font-medium underline"
              >
                Send another link
              </button>
            </div>
          ) : (
            <form onSubmit={handleMagicLinkRequest} className="space-y-3">
              <input
                id="magic-email"
                name="magic-email"
                type="email"
                required
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="Email address"
                value={magicEmail}
                onChange={(e) => setMagicEmail(e.target.value)}
              />
              <button
                type="submit"
                disabled={magicState === 'sending'}
                className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {magicState === 'sending' ? 'Sending...' : 'Send magic link'}
              </button>
            </form>
          )}
        </div>

        <div className="text-center">
          <p className="text-sm text-gray-600">
            Don't have an account?{' '}
            <Link
              to="/register"
              className="font-medium text-indigo-600 hover:text-indigo-500"
            >
              Sign up for free
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};
